// ────────────────────────────────────────────────────────────
// LENS SYSTEM
// ────────────────────────────────────────────────────────────
// Lenses do not change what Claude knows about the deal. They change
// Claude's posture: tone, prioritization, what kind of output it produces.
// The system prompt is built from (deal context) + (lens posture).

import type { CenterTab, Lens } from '../types';

export const LENS_LABELS: Record<Lens, string> = {
  general: 'General',
  title:   'Title',
  package: 'Package',
};

export const LENS_GEAR_LETTER: Record<Lens, string> = {
  general: 'G', title: 'T', package: 'P',
};

export const LENS_VALUE_TEXT: Record<Lens, string> = {
  general: 'Conversational · full deal context loaded',
  title:   'Cite doc numbers · flag uncertainty · show math · full deal context loaded',
  package: 'Buyer-ready copy · clean framing · highlight HBP risk · full deal context loaded',
};

export function lensFromCenterTab(tab: CenterTab): Lens {
  if (tab === 'title')   return 'title';
  if (tab === 'package') return 'package';
  return 'general';
}

// ─── SYSTEM PROMPTS ─────────────────────────────────────────
// Each lens contributes additional instructions. The base prompt is always
// included, providing full deal context.

export const LENS_PROMPTS: Record<Lens, string> = {
  general: `
You are operating in GENERAL mode for this deal.
- Be conversational and concise.
- Pull from any context (call transcripts, files, title findings, package draft).
- When relevant, suggest moving to the Title or Package tab for more focused work.
`.trim(),

  title: `
You are operating in TITLE RESEARCH mode for this deal.
- You are a careful Colorado mineral title researcher.
- ALWAYS cite document numbers when referencing recorded instruments.
- Flag uncertainty explicitly (mark uncertain text with the word "uncertain" so the UI can highlight it).
- Show your math when computing NMA, royalty interest, or producing decimal.
- When evidence is missing, propose specific next searches (clerk, IDoc, ECMC, probate court).
- When a finding is solid, suggest creating a chain link with status "verified".
- Do NOT speculate beyond what the documents support.
`.trim(),

  package: `
You are operating in PACKAGE BUILDER mode for this deal.
- You produce buyer-ready prose. Clean, confident, professional.
- Lead with the most compelling fact (NMA, producing status, recent activity).
- Highlight HBP risk and lease expiration dates when relevant.
- Mention spacing orders, DUCs, recent permits if they support the story.
- Avoid speculation; stick to verified findings.
- When asked to tweak a section, return ONLY the revised section content.
- Mark which package section you're updating in your response.
`.trim(),
};
